/** ToneJS based Wav player */

import { logger } from '@tunasong/models'
import loadAudio from 'audio-loader'
import createSamplePlayer from 'sample-player'
import type { Player } from 'soundfont-player'
import type { AudioContext } from 'standardized-audio-context'
import type { InputNode } from 'tone'
import type { MidiNoteEvent } from '../events/index.js'
import { midiToNote } from '../events/index.js'
import type { AudioInstrument } from './instrument.js'

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const instrumentNames = [
  'acoustic_grand_piano',
  'bright_acoustic_piano',
  'electric_grand_piano',
  'honkytonk_piano',
  'electric_piano_1',
  'electric_piano_2',
  'harpsichord',
  'clavinet',
  'celesta',
  'glockenspiel',
  'music_box',
  'vibraphone',
  'marimba',
  'xylophone',
  'tubular_bells',
  'dulcimer',
  'drawbar_organ',
  'percussive_organ',
  'rock_organ',
  'church_organ',
  'reed_organ',
  'accordion',
  'harmonica',
  'tango_accordion',
  'acoustic_guitar_nylon',
  'acoustic_guitar_steel',
  'electric_guitar_jazz',
  'electric_guitar_clean',
  'electric_guitar_muted',
  'overdriven_guitar',
  'distortion_guitar',
  'guitar_harmonics',
  'acoustic_bass',
  'electric_bass_finger',
  'electric_bass_pick',
  'fretless_bass',
  'slap_bass_1',
  'slap_bass_2',
  'synth_bass_1',
  'synth_bass_2',
  'violin',
  'viola',
  'cello',
  'contrabass',
  'tremolo_strings',
  'pizzicato_strings',
  'orchestral_harp',
  'timpani',
  'string_ensemble_1',
  'string_ensemble_2',
  'synth_strings_1',
  'synth_strings_2',
  'choir_aahs',
  'voice_oohs',
  'synth_choir',
  'orchestra_hit',
  'trumpet',
  'trombone',
  'tuba',
  'muted_trumpet',
  'french_horn',
  'brass_section',
  'synth_brass_1',
  'synth_brass_2',
  'soprano_sax',
  'alto_sax',
  'tenor_sax',
  'baritone_sax',
  'oboe',
  'english_horn',
  'bassoon',
  'clarinet',
  'piccolo',
  'flute',
  'recorder',
  'pan_flute',
  'blown_bottle',
  'shakuhachi',
  'whistle',
  'ocarina',
  'lead_1_square',
  'lead_2_sawtooth',
  'lead_3_calliope',
  'lead_4_chiff',
  'lead_5_charang',
  'lead_6_voice',
  'lead_7_fifths',
  'lead_8_bass__lead',
  'pad_1_new_age',
  'pad_2_warm',
  'pad_3_polysynth',
  'pad_4_choir',
  'pad_5_bowed',
  'pad_6_metallic',
  'pad_7_halo',
  'pad_8_sweep',
  'fx_1_rain',
  'fx_2_soundtrack',
  'fx_3_crystal',
  'fx_4_atmosphere',
  'fx_5_brightness',
  'fx_6_goblins',
  'fx_7_echoes',
  'fx_8_scifi',
  'sitar',
  'banjo',
  'shamisen',
  'koto',
  'kalimba',
  'bagpipe',
  'fiddle',
  'shanai',
  'tinkle_bell',
  'agogo',
  'steel_drums',
  'woodblock',
  'taiko_drum',
  'melodic_tom',
  'synth_drum',
  'reverse_cymbal',
  'guitar_fret_noise',
  'breath_noise',
  'seashore',
  'bird_tweet',
  'telephone_ring',
  'helicopter',
  'applause',
  'gunshot',
] as const

export type InstrumentName = (typeof instrumentNames)[number]

const SOUNDFONT = 'FluidR3_GM'

const soundfontUrl = (name: InstrumentName, format = 'mp3') =>
  `https://tunasong.com/instruments/soundfonts/${SOUNDFONT}/${name}-${format}.js`

/**
 * Global decoded-buffer cache (Performance: Samsung A9+ OOM — #1649, #1650).
 *
 * A single song mounts ~18 `usePlayNotes` consumers (chord popups, theory,
 * melody preview, stab scheduler, sidebar suggestions, song views …), each
 * constructing its OWN `Soundfont`. The expensive part is decoding the 2.5 MB
 * soundfont into ~88 AudioBuffers; doing it per instance spiked the JS heap to
 * ~286 MB and OOM-reloaded the tab.
 *
 * #1649 shared the load PER AudioContext, but the app runs several contexts
 * (app-level engine, per-page engine, engine re-inits), so the soundfont was
 * still decoded once per context (~20×). Decoded AudioBuffers are
 * context-independent at the pinned 44.1 kHz sample rate, so we cache the
 * decoded buffer map GLOBALLY (keyed by soundfont + instrument) and decode it
 * exactly once for the whole app. Each `Soundfont` then builds its own cheap
 * player from the shared buffers, routed to its own mixer node — which also
 * gives every consumer correct independent routing (the #1649 shared-player
 * caveat). Only a resolved decode is retained; a rejected one evicts its entry
 * so the next instance retries.
 */
type SampleBuffers = Record<string, AudioBuffer>
const decodedBuffers = new Map<string, Promise<SampleBuffers>>()

const bufferKey = (name: InstrumentName) => `${SOUNDFONT}:${name}`

/** Decode the soundfont ONCE, globally — shared across every AudioContext. */
const loadSharedBuffers = (context: AudioContext, name: InstrumentName): Promise<SampleBuffers> => {
  const key = bufferKey(name)
  let buffers = decodedBuffers.get(key)
  if (!buffers) {
    buffers = loadAudio(context, soundfontUrl(name))
    // Evict on failure so a later instance can retry the decode.
    buffers.catch(() => decodedBuffers.delete(key))
    decodedBuffers.set(key, buffers)
  }
  return buffers
}

/** Reset the global decoded-soundfont cache. Test-only seam. */
export const clearSoundfontCache = () => {
  decodedBuffers.clear()
}

/**
 * @see https://github.com/danigb/soundfont-player
 * @see https://github.com/gleitz/midi-js-soundfonts (soundfont files)
 * */
export class Soundfont implements AudioInstrument {
  player: Player | null = null
  /** Map of active AudioNodes indexed by MIDI note number */
  activeNotes: Record<number, AudioNode> = {}
  inputNode: InputNode | null = null

  /**
   * Single in-flight load (GH #1501). Without this memo, every event that
   * arrived while the player was still loading kicked off its OWN network
   * fetch + decode — a chord = N simultaneous noteons = N parallel loads,
   * each note starting whenever its load resolved (smeared onset).
   */
  #loadPromise: Promise<Player> | null = null

  constructor(
    private context: AudioContext,
    private name: InstrumentName
  ) {}

  async load() {
    /**
     * Decode the soundfont once globally, then build a per-instance player from
     * the shared AudioBuffers, routed to THIS instance's mixer node. The decode
     * is the expensive, shareable part; the player is cheap and per-instance, so
     * every consumer gets correct independent routing (#1649) while the 2.5 MB
     * soundfont is decoded exactly once for the whole app (#1650).
     */
    const buffers = await loadSharedBuffers(this.context, this.name)
    return createSamplePlayer(this.context, buffers, {}).connect(this.inputNode ?? this.context.destination)
  }

  connect(node: InputNode) {
    this.inputNode = node
    /** Preload so the first played note is warm — a cold soundfont cannot sound on time (GH #1501). */
    this.ensurePlayer().catch((e: unknown) => logger.warn(`Soundfont ${this.name} preload failed`, e))
  }
  /**
   * Counterpart to `connect()`/`ensurePlayer()`: stop any sounding notes, detach
   * the player's output node from the audio graph, and drop references so the
   * loaded player and its multi-MB decoded sample buffers can be garbage-collected.
   *
   * The song editor builds a fresh `Soundfont` per entity (keyed `${id}-${updatedAt}`),
   * so without this every opened/switched song leaked an instrument's worth of
   * audio memory — `disconnect()` was a no-op stub even though the engine calls it
   * on instrument teardown (GH #1644). Only this instrument's own nodes are touched;
   * the shared `AudioContext` (`engine.mixer.context`) is never disposed here.
   */
  disconnect(): void {
    try {
      this.player?.stop()
      // soundfont-player's Player exposes its output GainNode as `out` (not in the
      // public type). Detach it from everything downstream so no dangling node is
      // left connected in the graph.
      const out = (this.player as unknown as { out?: { disconnect?(): void } } | null)?.out
      out?.disconnect?.()
    } catch (e) {
      logger.warn(`Soundfont ${this.name} disconnect failed`, e)
    }
    this.activeNotes = {}
    this.player = null
    this.inputNode = null
    /** Reset the in-flight memo so a later `connect()` on a new mount reloads. */
    this.#loadPromise = null
  }
  /**
   * @param time when to play, in AudioContext-clock seconds — pass the exact
   * time the Tone transport hands to scheduled callbacks. Callbacks fire up
   * to the lookAhead (~100ms) BEFORE the deadline, so starting at "now"
   * (time 0) makes every note early by a variable amount (GH #1501).
   */
  async onEvent(event: MidiNoteEvent, time = 0) {
    const player = this.player ?? (await this.ensurePlayer())

    const { type, note } = event
    const name = midiToNote(note.number)

    /**  Handle events  */
    if (type === 'noteon') {
      const { value } = event
      const gain = typeof value === 'number' ? value : 1
      const audioNode = player.start(name, time, {
        gain,
        /** Parameters here */
      }) as unknown as AudioNode
      /** The type of AudioNode is wrong in the types */
      this.activeNotes[note.number] = audioNode
    } else if (type === 'noteoff') {
      const audioNode = this.activeNotes[note.number]
      if (!audioNode) {
        logger.warn(`No active note for ${note.number} (${name})`)
        return
      }
      /** Stop requires an array of IDs (the doc says AudioNode but that's wrong) */
      player.stop(time, [(audioNode as never)['id']] as never)
      delete this.activeNotes[note.number]
    } else {
      throw new Error(`Unknown event type ${type}`)
    }
  }

  /** Load the player exactly once; reset the memo on failure so a later event can retry. */
  private ensurePlayer = (): Promise<Player> => {
    this.#loadPromise ??= this.load().then(
      player => {
        this.player = player
        return player
      },
      (e: unknown) => {
        this.#loadPromise = null
        throw e
      }
    )
    return this.#loadPromise
  }
}
